var FIGURE = FIGURE || {};
// webpack prefers @vimeo/player's ES `module` entry, so require() hands back the { default }
// namespace rather than the constructor. `new` on the namespace throws, which aborted setup.
var VimeoPlayer = require('@vimeo/player');
VimeoPlayer = VimeoPlayer.default || VimeoPlayer;

/**
 * The hero carousel on /land and /wines.
 *
 * The text list is a scroll container with one item in front and most of a neighbour showing above
 * and below, drifting continuously through them rather than stepping. The list does not wrap: the
 * last item hands back to the first with a fade. Same mechanism and design as the homepage hero
 * (video-hero.js, _video-hero.scss); what differs is the clock behind it — playback position
 * there, the carousel's own interval here.
 *
 * Every slide gets the same slice, set by data-carousel-interval, whatever its copy or media.
 *
 * The window is sized in JS because it is built from the item heights: see measureGeometry().
 */
FIGURE.crossfadeCarousel = function () {
    // How long the list takes to shift from one item to the next. Paired with $carousel-slide-fade
    // in _crossfade-carousel.scss, which is the slide crossfade this moves in step with — change
    // one and change the other.
    var SHIFT_MS = 800;
    // Fraction of the remaining distance closed per frame while easing back onto the drift.
    var CATCHUP_RATE = 0.16;
    // Quiet time after the last scroll event before the list counts as settled. Long enough to sit
    // out trackpad momentum.
    var SCROLL_SETTLE_DELAY = 120;
    // Page-scroll stepping. Wheel events closer together than WHEEL_QUIET are one gesture, so a
    // trackpad's momentum tail steps once. WHEEL_STEP is how far a gesture travels before it steps;
    // SWIPE_STEP is the same for touch.
    var WHEEL_QUIET = 180;
    var WHEEL_STEP = 30;
    var SWIPE_STEP = 40;
    // How long a pause set by scrolling lasts after the last scroll step. A pause from the button
    // does not lapse.
    var SCROLL_RESUME_MS = 10000;
    // Hold on the first slide after load, on top of its own slice, before the carousel moves.
    var INTRO_HOLD_MS = 3000;

    function now() {
        return window.performance ? window.performance.now() : Date.now();
    }

    var primaryNav = document.querySelector('.primary-nav');
    var submenus = document.querySelectorAll('.submenu[popover]');

    document.querySelectorAll('[data-carousel]').forEach(function (carousel) {
        var slides = carousel.querySelectorAll('[data-carousel-slide]');
        var navItems = carousel.querySelectorAll('[data-carousel-item]');
        var nav = carousel.querySelector('[data-carousel-nav]');
        var track = carousel.querySelector('[data-carousel-track]');
        var liveRegion = carousel.querySelector('[data-carousel-live]');
        var countEl = carousel.querySelector('[data-carousel-count]');
        var itemCount = navItems.length;

        if (slides.length < 2) return;
        if (!track || !nav || !itemCount) return;

        // How long each item spends in front. The same for every slide.
        var interval = parseInt(carousel.getAttribute('data-carousel-interval'), 10) || 9000;
        // One full pass. The clock is kept inside this, so the wrap is a modulo.
        var cycle = interval * itemCount;
        // How long the shift after each handover takes; see shiftAt().
        var shiftSpan = Math.min(SHIFT_MS, interval / 2);

        var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        var reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

        var current = 0;
        var pauseCount = 0;
        var userPaused = reducedMotion;
        var onScreen = true;
        var isMuted = true;

        // The list is linear: the last item hands back to the first by fading the text out, jumping
        // and fading in, rather than travelling on. See wrapFade().
        var slots = Array.prototype.slice.call(navItems);

        // Milliseconds into the cycle, and the wall time it was last advanced at.
        var clock = frontAt(0);
        var clockAt = now();
        // Scroll offset that centres each slot. Cached because the loop must not read layout: an
        // offsetTop read after writing scrollTop forces a reflow, sixty times a second.
        var offsets = [];
        // Whether the drift owns the scroll position. Survives a pause, so a paused carousel holds
        // where it was frozen rather than being handed mid-handover to snap.
        var owned = false;
        var rafId = null;
        var catchUp = false;
        var opacityPainted = {};
        var fallbackTimer = null;

        /**
         * Geometry.
         *
         * The window is one item tall plus a slack's worth of room at each end — the slack is the
         * only place the neighbours are ever seen. The track is padded by a slack at both ends so
         * the outermost slots can still reach the centre; a scroll container cannot be pushed past
         * its own content the way the old transform could.
         */

        // Read from the stylesheet rather than duplicated, which is why those values are px there:
        // a custom property comes back as specified.
        function navMetric(name) {
            return parseFloat(window.getComputedStyle(nav).getPropertyValue(name)) || 0;
        }

        function clearItemHeights() {
            for (var i = 0; i < slots.length; i++) {
                slots[i].style.height = '';
            }
        }

        // The hero, less what the site header covers and the nav's own bottom inset.
        function availableHeight() {
            var siteHeader = document.querySelector('.site-header');
            var carouselRect = carousel.getBoundingClientRect();
            var topClearance = siteHeader
                ? Math.max(0, siteHeader.getBoundingClientRect().bottom - carouselRect.top)
                : 0;
            var navBottom = parseFloat(window.getComputedStyle(nav).bottom) || 0;
            return carousel.offsetHeight - topClearance - navBottom;
        }

        // Every item is given the height of the tallest, so what shows either side of the item in
        // front is the same wherever the drift has got to — and so the pitch is constant, which the
        // drift's arithmetic depends on.
        //
        // The slack is fitted rather than fixed: the preferred value where it fits, narrowing
        // toward the floor below that, null when even the floor will not (compact mode). See the
        // note above --hero-slack in _crossfade-carousel.scss.
        //
        // Remeasured on every pass, because item heights move with the viewport's *width* as the
        // copy rewraps. Both the heights and compact mode have to come off first or the answer is
        // read back off the last pass — a window that could never shrink, and a single item that
        // fits anywhere. Nothing paints in between.
        function measureGeometry() {
            var wasCompact = nav.classList.contains('is-compact');
            if (wasCompact) nav.classList.remove('is-compact');
            clearItemHeights();

            var tallest = 0;
            for (var i = 0; i < itemCount; i++) {
                tallest = Math.max(tallest, navItems[i].offsetHeight);
            }

            var slackMax = navMetric('--hero-slack');
            var slackMin = navMetric('--hero-slack-min');
            var room = Math.floor((availableHeight() - tallest) / 2);

            if (wasCompact) nav.classList.add('is-compact');

            var slack = Math.min(slackMax, room);
            if (slack < slackMin) return null;

            return {
                itemHeight: tallest,
                height: tallest + 2 * slack,
                pad: slack
            };
        }

        function applyGeometry(geometry) {
            for (var i = 0; i < slots.length; i++) {
                slots[i].style.height = geometry.itemHeight + 'px';
            }
            nav.style.height = geometry.height + 'px';
            track.style.paddingTop = geometry.pad + 'px';
            track.style.paddingBottom = geometry.pad + 'px';
            cacheOffsets();
        }

        function cacheOffsets() {
            offsets = [];
            for (var i = 0; i < slots.length; i++) {
                offsets.push(Math.round(
                    slots[i].offsetTop - (nav.clientHeight - slots[i].offsetHeight) / 2
                ));
            }
        }

        // Where the list has to be scrolled to for `index` to be the item in front.
        function offsetFor(index) {
            var item = navItems[index];
            if (!item) return 0;
            return Math.round(item.offsetTop - (nav.clientHeight - item.offsetHeight) / 2);
        }

        function setPosition(index, animate) {
            // Compact mode shows the item in front on its own: nothing to scroll to.
            if (nav.classList.contains('is-compact')) return;
            // Two authors on one value — the drift would overwrite this within 16ms. goTo() moves
            // the clock instead and lets the loop carry the list there.
            if (owned) return;

            var target = offsetFor(index);
            if (Math.abs(nav.scrollTop - target) < 1) return;

            if (animate) {
                // Assigned rather than scrollTo'd so scroll-behavior decides whether it glides,
                // which is what the reduced-motion block switches off.
                nav.scrollTop = target;
                return;
            }

            nav.scrollTo({ top: target, behavior: 'instant' });
        }

        /**
         * The clock.
         *
         * Every item owns an equal slice of the cycle and is centred at the start of its own, so
         * the list travels exactly one item per slice and its position is defined for every instant
         * in between.
         *
         * The slide changes at the *midpoint* of the slice, and the list holds still on either
         * side of it: the shift runs from that midpoint for exactly as long as the crossfade
         * beneath it, so the words move while the picture dissolves and are settled before it is
         * done. The item in front is therefore centred and unblurred for the rest of its slice,
         * rather than easing through the whole of it.
         */

        // 6p^5 - 15p^4 + 10p^3: zero velocity and acceleration at both ends, so the list leaves
        // the hold and arrives at the next one without a jolt at either edge.
        function smootherstep(p) {
            if (p <= 0) return 0;
            if (p >= 1) return 1;
            return p * p * p * (p * (p * 6 - 15) + 10);
        }

        // The slice mapped onto the shift window that opens at the midpoint: flat at 0 before it,
        // flat at 1 after. Capped at the half slice the midpoint leaves, so a short slice shortens
        // the shift rather than letting it run past the next handover.
        function shiftAt(p) {
            return smootherstep((p - 0.5) / (shiftSpan / interval));
        }

        // The clock at the moment `index` has just settled in front, after the handover to it.
        // Landing a slide here rather than at its slice start gives it a full slice, not half.
        function frontAt(index) {
            return ((index - 0.5) * interval + shiftSpan + cycle) % cycle;
        }

        // Which item is handing over at `ms` into the cycle, and how far through.
        function driftAt(ms) {
            var index = Math.min(itemCount - 1, Math.floor(ms / interval));
            var p = Math.min(1, Math.max(0, (ms - index * interval) / interval));
            return { index: index, p: p, eased: shiftAt(p) };
        }

        function isWrap(drift) {
            return drift.index === itemCount - 1;
        }

        // The last item's handover. The text fades out over the first half of the shift and back in
        // over the second, and the list jumps to the first item while it is invisible.
        function wrapFade(drift) {
            return Math.abs(1 - 2 * drift.eased);
        }

        function offsetAt(drift) {
            var from = offsets[drift.index];
            if (from === undefined) return 0;
            if (isWrap(drift)) return drift.eased < 0.5 ? from : offsets[0];
            return from + (offsets[drift.index + 1] - from) * drift.eased;
        }

        var trackOpacity = null;
        function setTrackOpacity(value) {
            if (value === trackOpacity) return;
            trackOpacity = value;
            track.style.opacity = value === null ? '' : String(value);
        }

        // Capped at a slice: nothing here is in step with anything external, so after a stall the
        // list carries on from where it was rather than whipping through several items to catch up
        // with a wall clock.
        function advanceClock() {
            var stamp = now();
            var delta = stamp - clockAt;
            clockAt = stamp;
            if (delta < 0) delta = 0;
            if (delta > interval) delta = interval;
            clock = (clock + delta) % cycle;
        }

        // The item's place on the colour ramp — 0 recessive, 1 in front. The stylesheet turns it
        // into a blur; the ink itself never moves, so there is one contrast number for the whole
        // list. An inline custom property outranks the base rule and .active exactly as an inline
        // opacity did, so it still has to be cleared when the list is handed back; see release().
        function setInk(key, el, value) {
            if (!el || opacityPainted[key] === value) return;
            opacityPainted[key] = value;
            if (value === null) el.style.removeProperty('--hero-t');
            else el.style.setProperty('--hero-t', String(value));
        }

        // Only the two items in a handover are written; the rest are left to the stylesheet. They
        // are mirror images — the one leaving is at 1 - eased, the one arriving at eased — so the
        // pair crosses at the midpoint and the list always holds one item's worth of ink.
        function paintDrift(drift) {
            var leaving = drift.index;
            var arriving = (leaving + 1) % itemCount;
            for (var i = 0; i < slots.length; i++) {
                setInk(i, slots[i], i === leaving ? 1 - drift.eased : (i === arriving ? drift.eased : null));
            }
            setTrackOpacity(isWrap(drift) && drift.eased > 0 && drift.eased < 1 ? wrapFade(drift) : null);
        }

        function clearPaint() {
            for (var i = 0; i < slots.length; i++) {
                setInk(i, slots[i], null);
            }
            setTrackOpacity(null);
        }

        // Which item is in front, and the slide that goes with it.
        function setActive(index, animate, announce) {
            if (index === current) return;

            var previous = current;

            slides[current].classList.remove('active');
            slides[current].setAttribute('aria-hidden', 'true');
            navItems[current].classList.remove('active');
            navItems[current].setAttribute('aria-pressed', 'false');

            current = index;

            slides[current].classList.add('active');
            slides[current].removeAttribute('aria-hidden');
            navItems[current].classList.add('active');
            navItems[current].setAttribute('aria-pressed', 'true');

            // Unconditionally, unlike the announcement below: the counter is read by eye, and a
            // position that only tracked the moves the visitor made themselves would be wrong for
            // every automatic advance in between. The template renders the opening 1/N, so this is
            // only ever an update.
            if (countEl) countEl.textContent = (current + 1) + '/' + slides.length;

            // Only for a move the visitor made that left focus where it was — an arrow step, or a
            // gesture in static mode. Announcing the automatic advance would read a title every few
            // seconds for as long as the page is open; a click needs no help, since focus is on the
            // item and has just been read.
            if (announce && liveRegion) {
                var titleEl = navItems[current].querySelector('[data-carousel-title]');
                liveRegion.textContent = titleEl ? titleEl.textContent.trim() : '';
            }

            applyMuted();
            syncVideo(previous);
            setPosition(current, animate !== false);
        }

        /**
         * The drift loop.
         *
         * `owned` says the drift is responsible for the scroll position, and carries .is-drifting,
         * which takes smooth scrolling out of the way — it queues an animation per assignment
         * rather than composing them. Ownership survives a pause; the loop itself does not.
         */

        // Whether the drift may own the list at all, as against whether there is any point running
        // it right now (canRun). A carousel scrolled out of view is the second, not the first.
        function mustRelease() {
            return itemCount < 2
                || reducedMotion
                || nav.classList.contains('is-compact');
        }

        function canRun() {
            return pauseCount === 0 && !userPaused && onScreen && !document.hidden;
        }

        function frame() {
            rafId = null;
            // Bailing cannot strand the loop: every path that changes canRun() calls updateDrift(),
            // which restarts it. Rescheduling instead would repaint a frozen position forever.
            if (!owned || !canRun()) return;

            advanceClock();

            var drift = driftAt(clock);
            var target = offsetAt(drift);

            // The wrap jumps while the text is faded out; easing would scroll back through the list.
            if (isWrap(drift) && drift.eased > 0) catchUp = false;

            if (catchUp) {
                // Easing onto the drift from wherever the list was left. Exponential, so it is
                // quick where the gap is wide and unhurried as it closes.
                var gap = target - nav.scrollTop;
                if (Math.abs(gap) < 1) {
                    catchUp = false;
                } else {
                    nav.scrollTop = nav.scrollTop + gap * CATCHUP_RATE;
                }
            }

            if (!catchUp && !jumpPending && Math.abs(nav.scrollTop - target) >= 0.5) {
                nav.scrollTop = target;
            }

            paintDrift(drift);

            // Slot to real item: the slot arriving past the end is item 0's copy, so the slide and
            // the lit state come back round to item 0 with it.
            var nearest = (drift.index + (drift.p >= 0.5 ? 1 : 0)) % itemCount;
            if (nearest !== current) setActive(nearest, false);

            schedule();
        }

        function schedule() {
            if (rafId === null) rafId = window.requestAnimationFrame(frame);
        }

        // Takes the scroll position. catchUp is armed on acquiring it only — re-arming it on the
        // way past would leave the drift permanently easing toward a moving target.
        function claim() {
            if (!owned) {
                owned = true;
                nav.classList.add('is-drifting');
                catchUp = true;
            }
            // Resuming: the clock must not count the time it stood still.
            if (rafId === null) clockAt = now();
            schedule();
        }

        // Stops animating but keeps the position, for anything that will hand back to the same
        // drift where it left off.
        function suspend() {
            if (rafId !== null) {
                window.cancelAnimationFrame(rafId);
                rafId = null;
            }
            // Stopped mid-wrap, the text would stay faded out. Settle it on the first item, which
            // is already the one in front by then.
            if (trackOpacity !== null) {
                clock = 0;
                nav.scrollTo({ top: offsets[0] || 0, behavior: 'instant' });
                paintDrift(driftAt(clock));
            }
        }

        // Gives the list up: to compact mode, to reduced motion. The inline paint goes with it or
        // it would outrank the stylesheet's own .active and hover states.
        function release() {
            suspend();
            if (!owned) return;
            owned = false;
            nav.classList.remove('is-drifting');
            catchUp = false;
            cancelFadeJump();
            clearPaint();
        }

        // Where the drift cannot run, the carousel still advances one item per slice and cuts at
        // the boundary — the behaviour the drift replaced. Compact mode is the case that needs it;
        // reduced motion is the exception that does not, which is why this is not simply tied to
        // mustRelease(). The clock moves with it, so growing the viewport back hands the drift a
        // position that is already right.
        function startFallback() {
            if (fallbackTimer !== null) return;
            fallbackTimer = window.setInterval(function () {
                var next = (current + 1) % itemCount;
                clock = frontAt(next);
                clockAt = now();
                setActive(next, true);
            }, interval);
        }

        function stopFallback() {
            if (fallbackTimer === null) return;
            window.clearInterval(fallbackTimer);
            fallbackTimer = null;
        }

        function updateDrift() {
            if (mustRelease()) {
                release();
                // Nothing else will move the list, so a gesture has to be able to. Compact mode
                // shows one item and needs no such thing.
                nav.classList.toggle('is-static', !nav.classList.contains('is-compact'));
                setPosition(current, false);
                if (!reducedMotion && canRun()) {
                    startFallback();
                } else {
                    stopFallback();
                }
                return;
            }
            stopFallback();
            nav.classList.remove('is-static');
            // Paused, hidden or off screen: hold the position and the lighting as they are.
            if (!canRun()) {
                suspend();
                return;
            }
            claim();
        }

        // Moving the list by hand. There is no second clock to hand back to the way the homepage
        // hands its list back to the video, so nothing detaches or resyncs: the clock rewinds to
        // the target's slice start and the drift carries on, which restarts the dwell as
        // resetTimer() used to.
        // A jump cuts while the text is faded out, as the automatic wrap does. The drift leaves the
        // scroll position alone until then, so the cut cannot show.
        var fadeOut = null;
        var fadeIn = null;
        var jumpPending = false;

        function cancelFadeJump() {
            jumpPending = false;
            if (fadeOut) fadeOut.cancel();
            if (fadeIn) fadeIn.cancel();
            fadeOut = fadeIn = null;
        }

        function fadeJump(index) {
            if (!track.animate) {
                nav.scrollTo({ top: offsetFor(index), behavior: 'instant' });
                return;
            }
            var half = SHIFT_MS / 2;
            var out = fadeOut = track.animate(
                [{ opacity: 1 }, { opacity: 0 }],
                { duration: half, easing: 'ease-in', fill: 'forwards' }
            );
            jumpPending = true;
            out.finished.then(function () {
                if (fadeOut !== out) return;
                jumpPending = false;
                nav.scrollTo({ top: offsetFor(index), behavior: 'instant' });
                // Started before the fade-out is dropped, so no frame shows the text at full.
                fadeIn = track.animate([{ opacity: 0 }, { opacity: 1 }], { duration: half, easing: 'ease-out' });
                out.cancel();
                fadeOut = null;
            }).catch(function () { });
        }

        function goTo(index, announce) {
            if (index === current || index < 0 || index >= itemCount) return;

            // More than one item along is cut rather than travelled: dragging the whole list past a
            // window this size reads as a blur of everything in between.
            var jump = Math.abs(index - current) > 1;

            clock = frontAt(index);
            clockAt = now();

            if (owned) {
                cancelFadeJump();
                if (jump) {
                    fadeJump(index);
                    catchUp = false;
                } else {
                    catchUp = true;
                    // Paused, the loop is not running to carry the list there, so move it here.
                    if (!canRun()) nav.scrollTo({ top: offsetFor(index), behavior: 'smooth' });
                }
            }

            setActive(index, !jump, announce);
            // Dropped so updateDrift() starts a fresh one, rather than giving the item just chosen
            // whatever was left of the last one's slice.
            stopFallback();
            updateDrift();
            // Nor is it there to move the lit state off the item that was in front.
            if (owned && !canRun()) paintDrift(driftAt(clock));
        }

        function step(delta) {
            goTo((current + delta + itemCount) % itemCount, true);
        }

        function pause() {
            pauseCount++;
            updateDrift();
        }

        function resume() {
            pauseCount = Math.max(0, pauseCount - 1);
            updateDrift();
        }

        // The visitor's own pause, which the pause button shows.
        var scrollResumeTimer = null;
        function setUserPaused(paused) {
            clearTimeout(scrollResumeTimer);
            scrollResumeTimer = null;
            userPaused = paused;
            if (pauseBtn) {
                pauseBtn.classList.toggle('is-paused', paused);
                pauseBtn.setAttribute('aria-label', paused ? 'Play slideshow' : 'Pause slideshow');
            }
            updateDrift();
        }

        // Scrolling pauses too, but only until the visitor stops. It leaves a firm pause alone.
        function pauseForScroll() {
            if (userPaused && scrollResumeTimer === null) return;
            setUserPaused(true);
            scrollResumeTimer = setTimeout(function () {
                setUserPaused(false);
            }, SCROLL_RESUME_MS);
        }

        // Delegated, so one listener covers every item.
        track.addEventListener('click', function (e) {
            var item = e.target.closest('[data-carousel-item]');
            if (!item) return;
            var slideIndex = parseInt(item.getAttribute('data-slide'), 10);
            if (!isNaN(slideIndex)) goTo(slideIndex);
        });

        nav.addEventListener('keydown', function (e) {
            if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
                e.preventDefault();
                step(1);
            } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
                e.preventDefault();
                step(-1);
            }
        });

        // Focus brings its item forward, rather than the browser scrolling it into view and the
        // drift taking it out again next frame. The drift is held while focus is inside, so the
        // list cannot move out from under a keyboard visitor.
        //
        // focusin fires again on every move between items, hence the flag: without it each move
        // would add a pause the single focusout could never balance.
        var navFocused = false;
        nav.addEventListener('focusin', function (e) {
            if (!navFocused) {
                navFocused = true;
                pause();
            }
            var item = e.target.closest('[data-carousel-item]');
            if (!item) return;
            var slideIndex = parseInt(item.getAttribute('data-slide'), 10);
            if (!isNaN(slideIndex)) goTo(slideIndex);
        });

        nav.addEventListener('focusout', function (e) {
            if (nav.contains(e.relatedTarget)) return;
            if (!navFocused) return;
            navFocused = false;
            resume();
        });

        // Which item a gesture came to rest on — only in static mode, the one state that still lets
        // one move the list. Debounced, so a flick's momentum tail is read once where it stops.
        var scrollSettleTimer;
        nav.addEventListener('scroll', function () {
            if (!nav.classList.contains('is-static')) return;
            clearTimeout(scrollSettleTimer);
            scrollSettleTimer = setTimeout(function () {
                var centre = nav.scrollTop + nav.clientHeight / 2;
                var nearest = 0;
                var shortest = Infinity;
                for (var i = 0; i < itemCount; i++) {
                    var distance = Math.abs(
                        navItems[i].offsetTop + navItems[i].offsetHeight / 2 - centre
                    );
                    if (distance < shortest) {
                        shortest = distance;
                        nearest = i;
                    }
                }
                // The clock moves with it, so pressing play carries on from the item in front.
                // animate: false because the list is already there — this records where the visitor
                // put it. Announced for the same reason an arrow step is: focus went nowhere.
                if (nearest !== current) {
                    clock = frontAt(nearest);
                    clockAt = now();
                    setActive(nearest, false, true);
                }
            }, SCROLL_SETTLE_DELAY);
        }, { passive: true });

        /**
         * Scrolling the page over the hero steps the carousel, one item per gesture. At the end of
         * the list in that direction the gesture goes to the page instead, so the footer is still
         * reachable, and once the page has scrolled off the hero's top, scrolling is the page's.
         */
        function scrollSteps(delta, target) {
            if (!delta) return false;
            if (target.closest('dialog')) return false;
            // Static mode already lets a gesture scroll the list itself.
            if (nav.classList.contains('is-static') && nav.contains(target)) return false;
            if (carousel.getBoundingClientRect().top < -1) return false;
            return delta > 0 ? current < itemCount - 1 : current > 0;
        }

        // The wheel gesture in progress. Decided once at its start and held for its whole momentum
        // tail, so a page scroll that arrives at the hero cannot turn into a step partway through.
        var wheelAt = 0;
        var wheelOwned = false;
        var wheelStepped = false;
        var wheelSteppedAt = 0;
        var wheelTravel = 0;
        var wheelLast = 0;

        carousel.addEventListener('wheel', function (e) {
            if (e.ctrlKey) return; // pinch zoom
            var dy = e.deltaY * (e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? window.innerHeight : 1);
            if (!dy || Math.abs(dy) < Math.abs(e.deltaX)) return;

            var stamp = now();
            var fresh = stamp - wheelAt > WHEEL_QUIET;
            // A new swipe inside the last one's momentum: the deltas climb rather than decay.
            var renewed = wheelOwned && wheelStepped
                && Math.abs(dy) > Math.abs(wheelLast) * 1.5
                && stamp - wheelSteppedAt > SHIFT_MS;
            wheelAt = stamp;
            wheelLast = dy;

            if (fresh || renewed) {
                wheelOwned = scrollSteps(dy, e.target);
                wheelStepped = false;
                wheelTravel = 0;
            }
            if (!wheelOwned) return;

            e.preventDefault();
            if (wheelStepped) return;
            wheelTravel += dy;
            if (Math.abs(wheelTravel) < WHEEL_STEP) return;
            wheelStepped = true;
            wheelSteppedAt = stamp;
            pauseForScroll();
            goTo(current + (wheelTravel > 0 ? 1 : -1), true);
        }, { passive: false });

        // Touch decides on the first real movement, since that is the last chance to stop the page
        // scrolling, and steps on release.
        var touchX = null;
        var touchY = null;
        var touchOwned = null;

        function resetTouch() {
            touchX = touchY = touchOwned = null;
        }

        carousel.addEventListener('touchstart', function (e) {
            if (e.touches.length !== 1) return resetTouch();
            touchX = e.touches[0].clientX;
            touchY = e.touches[0].clientY;
            touchOwned = null;
        }, { passive: true });

        carousel.addEventListener('touchmove', function (e) {
            if (touchY === null) return;
            var dx = touchX - e.touches[0].clientX;
            var dy = touchY - e.touches[0].clientY;
            if (touchOwned === null) {
                if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
                touchOwned = Math.abs(dy) > Math.abs(dx) && scrollSteps(dy, e.target);
            }
            if (touchOwned) e.preventDefault();
        }, { passive: false });

        carousel.addEventListener('touchend', function (e) {
            if (touchOwned) {
                var dy = touchY - e.changedTouches[0].clientY;
                if (Math.abs(dy) >= SWIPE_STEP) {
                    pauseForScroll();
                    goTo(current + (dy > 0 ? 1 : -1), true);
                }
            }
            resetTouch();
        });

        carousel.addEventListener('touchcancel', resetTouch);

        if (primaryNav) {
            primaryNav.addEventListener('mouseenter', pause);
            primaryNav.addEventListener('mouseleave', resume);
        }

        submenus.forEach(function (submenu) {
            submenu.addEventListener('toggle', function (e) {
                e.newState === 'open' ? pause() : resume();
            });
        });

        var prevBtn = carousel.querySelector('[data-carousel-prev]');
        var nextBtn = carousel.querySelector('[data-carousel-next]');
        var pauseBtn = carousel.querySelector('[data-carousel-pause]');
        var muteBtn = carousel.querySelector('[data-carousel-mute]');
        var dimBtn = carousel.querySelector('[data-carousel-dim]');
        var videos = Array.from(carousel.querySelectorAll('video'));
        var vimeoEmbeds = Array.from(carousel.querySelectorAll('[data-carousel-video]')).map(function (iframe) {
            // The markup ships these without a src so the still frame under each one is what
            // paints first — see the note in crossfade-carousel.tpl.php. Promoted here, before
            // the player is constructed: @vimeo/player reads the src attribute synchronously
            // and throws on an iframe that has none.
            iframe.src = iframe.getAttribute('data-src');

            var embed = { iframe: iframe, player: new VimeoPlayer(iframe), frame: iframe.parentNode };

            // Fades the embed up over the still, once and for good. On the first play rather
            // than on ready(), because a ready player still shows Vimeo's dark box until it has
            // a frame to draw; and never taken off again, because leaving a slide seeks its clip
            // rather than unloading it, so there is nothing to cover on the way back.
            embed.player.on('play', function () {
                if (embed.frame) embed.frame.classList.add('is-playing');
            });

            return embed;
        });

        // One mute setting for the carousel, but audio only from the slide on screen: hidden slides
        // keep playing, so unmuting every player at once would stack their audio. Called on each
        // slide change too, so leaving a video silences it and arriving at one picks it up.
        function applyMuted() {
            var activeSlide = slides[current];
            videos.forEach(function (v) {
                v.muted = isMuted || !activeSlide.contains(v);
            });
            vimeoEmbeds.forEach(function (embed) {
                embed.player.setMuted(isMuted || !activeSlide.contains(embed.iframe)).catch(function () { });
            });
            if (!muteBtn) return;
            muteBtn.classList.toggle('is-unmuted', !isMuted);
            muteBtn.setAttribute('aria-label', isMuted ? 'Unmute' : 'Mute');
        }

        // Which embed belongs to each slide, so a handover can find the clip it is arriving at.
        var slideVideo = [];
        vimeoEmbeds.forEach(function (embed) {
            for (var i = 0; i < slides.length; i++) {
                if (slides[i].contains(embed.iframe)) {
                    slideVideo[i] = embed;
                    return;
                }
            }
        });

        // Clips loop in the background and do not hold their slide; every slide gets the same
        // slice. The one arriving is rewound so it plays from the top each time round.
        function syncVideo(previous) {
            var leaving = slideVideo[previous];
            if (leaving) leaving.player.setCurrentTime(0).catch(function () { });

            var arriving = slideVideo[current];
            if (!arriving) return;
            arriving.player.setCurrentTime(0).then(function () {
                return arriving.player.play();
            }).catch(function () { });
        }

        // The dim layer. Deliberately not remembered between loads: this carousel starts undimmed,
        // as the homepage hero does, so every arrival comes back to the bare slide and dimming is a
        // momentary choice about the view in front of you. What the bright default trades away, and
        // what covers for it, is set out at .crossfade-carousel::before in _crossfade-carousel.scss.
        // Nothing here touches the slideshow — it is a layer over it.
        if (dimBtn) {
            dimBtn.addEventListener('click', function () {
                var undimmed = carousel.classList.toggle('is-undimmed');
                // Kept in step with the tooltip the same class switches, which is this button's
                // visible label; the two have to read alike for voice control.
                dimBtn.setAttribute('aria-label', undimmed ? 'Dim slideshow' : 'Brighten slideshow');
            });
        }

        if (prevBtn) {
            prevBtn.addEventListener('click', function () {
                step(-1);
            });
        }

        if (nextBtn) {
            nextBtn.addEventListener('click', function () {
                step(1);
            });
        }

        if (pauseBtn) {
            if (reducedMotion) {
                pauseBtn.classList.add('is-paused');
                pauseBtn.setAttribute('aria-label', 'Play slideshow');
            }
            pauseBtn.addEventListener('click', function () {
                // Pressing play is an explicit ask for the movement the preference switched off,
                // and takes the list out of static mode with it.
                if (userPaused) reducedMotion = false;
                setUserPaused(!userPaused);
            });
        }

        if (muteBtn) {
            muteBtn.addEventListener('click', function () {
                isMuted = !isMuted;
                applyMuted();
            });
        }

        applyMuted();

        var resizeTimer;
        function checkGeometry() {
            var geometry = measureGeometry();

            // Too short for the window even at its narrowest. Everything measured is cleared with
            // it: only the item in front is displayed, so padding sized to centre it would be
            // scrollable space around nothing.
            if (!geometry) {
                nav.classList.add('is-compact');
                nav.style.height = '';
                track.style.paddingTop = '';
                track.style.paddingBottom = '';
                clearItemHeights();
                updateDrift();
                return;
            }

            // Applied on every pass, not only on the way out of compact mode: a width change
            // rewraps the copy, so an early return on the mode alone would leave the window sized
            // for the viewport before last.
            nav.classList.remove('is-compact');
            applyGeometry(geometry);
            setPosition(current, false);
            // Every offset the drift interpolates between has just moved, so the position it holds
            // belongs to the old layout. Ease onto the new one rather than jumping next frame.
            catchUp = true;
            updateDrift();
        }

        // Counted from when the page is first seen, so a tab opened in the background still gets it.
        // Set directly rather than through pause(): checkGeometry() runs the first updateDrift().
        pauseCount++;
        function startIntroHold() {
            if (document.hidden) return;
            document.removeEventListener('visibilitychange', startIntroHold);
            setTimeout(resume, INTRO_HOLD_MS);
        }
        document.addEventListener('visibilitychange', startIntroHold);
        startIntroHold();

        checkGeometry();

        // Slide 0 is active in the markup, so no handover ran for it.
        syncVideo(-1);

        window.addEventListener('resize', function () {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(checkGeometry, 150);
        });

        // The heights are first measured before webfonts have necessarily settled, and a face
        // swapping rewraps the copy. On a short viewport this is the difference between compact
        // mode and the effect.
        if (document.fonts && document.fonts.ready) {
            document.fonts.ready.then(checkGeometry);
        }

        // A preference, not a build-time constant: honouring a change while the page is open is the
        // difference between the setting working and working after a reload.
        function onMotionPreference(e) {
            reducedMotion = e.matches;
            setUserPaused(e.matches);
        }

        if (reduceMotionQuery.addEventListener) {
            reduceMotionQuery.addEventListener('change', onMotionPreference);
        } else if (reduceMotionQuery.addListener) {
            reduceMotionQuery.addListener(onMotionPreference);
        }

        // Driving a scroll container nobody is looking at costs a write, a layout and a scroll
        // event per frame. The position is picked up again on the way back.
        if (window.IntersectionObserver) {
            new window.IntersectionObserver(function (entries) {
                onScreen = entries[entries.length - 1].isIntersecting;
                updateDrift();
            }).observe(carousel);
        }

        // rAF stops being served to a hidden tab, so the loop pauses itself and picks up where it
        // left off rather than jumping to where a wall clock says it should be.
        document.addEventListener('visibilitychange', updateDrift);
    });
}();
